import React, { useEffect, useRef, useState, useCallback } from "react";
import Button from "../../components/form/Button";
import Input from "../../components/form/Input";
import Image from "../../components/elements/Image";
import PayCustomAmount from "../../components/sections/SplitBillModals/PayCustomAmount";
import PayForYourItem from "../../components/sections/SplitBillModals/PayForYourItem";
import DivideBillEqualy from "../../components/sections/SplitBillModals/DivideBillEqualy";
import { Link, useParams } from 'react-router-dom';

import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import CheckoutForm from "../components/stripe/CheckoutForm";
import { apiRequest } from "../../util/apiUtils";
import { EmptyBillingState, BillItemSkeleton } from "../components/loaders/MenuSkeletons";
import { useBistroNavigation } from "../../hooks/useBistroNavigation";
import DivideBillEqualyNew from "../../components/sections/SplitBillModals/DivideBillEqualyNew";

const stripePromise = loadStripe("pk_test_51SOCw47rV8gdwKZcKmtSE04aitFEZ5loPR5p5efhSsHAa28YdC4Qj0qAK47mitwHE9DERHbz9wBK4XZjNlpNpOAL00bx7Hh4Le");

const BillPaySkeleton = () => (
  <div className="w-full max-w-sm mx-auto bg-white rounded-2xl shadow-sm overflow-hidden pb-6">
    <div className="border-b border-[#F4F4F4] px-4 py-5">
      <div className="flex items-center justify-between gap-4">
        <div className="w-10 h-10 bg-gray-200 rounded animate-pulse"></div>
        <div className="flex-1">
          <div className="h-5 bg-gray-200 rounded w-24 mb-1 animate-pulse"></div>
        </div>
        <div className="h-4 bg-gray-200 rounded w-20 animate-pulse"></div>
      </div>
    </div>
    
    <div className="my-3 border-b border-gray-200 px-4 py-3">
      <BillItemSkeleton />
      <BillItemSkeleton />
      <BillItemSkeleton />
    </div>
    
    <div className="px-4 mb-4">
      <div className="flex justify-between items-center">
        <div className="flex-1">
          <div className="h-4 bg-gray-200 rounded w-32 mb-1 animate-pulse"></div>
          <div className="h-3 bg-gray-200 rounded w-40 animate-pulse"></div>
        </div>
        <div className="h-6 bg-gray-200 rounded w-16 animate-pulse"></div>
      </div>
    </div>
    
    <div className="px-4 mb-4">
      <div className="flex gap-3">
        <div className="flex-1 h-12 bg-gray-200 rounded-full animate-pulse"></div>
        <div className="flex-1 h-12 bg-gray-200 rounded-full animate-pulse"></div>
      </div>
    </div>
    
    <div className="px-4">
      <div className="rounded-xl border border-gray-200 p-4 bg-slate-50/30">
        <div className="h-4 bg-gray-200 rounded w-48 mb-3 animate-pulse"></div>
        <div className="grid grid-cols-4 gap-2 mb-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="aspect-square bg-gray-200 rounded-xl animate-pulse"></div>
          ))}
        </div>
        <div className="h-10 bg-gray-200 rounded-lg animate-pulse"></div>
      </div>
    </div>
  </div>
);

// Move PaymentContent OUTSIDE of BillPay component
const PaymentContent = ({ 
  selectedTip, 
  handleTipSelect, 
  customTip, 
  handleCustomTipChange,
  payableAmount,
  paymentType,
  tableNumber,
  orderId,
  tipAmount,
  remainingAmount,
  hasPartialPayments,
  subtotalAmount
}) => (
  <div className="mt-0">
    <div className="rounded-xl border border-gray-200 px-3 shadow-sm pt-5 pb-3 bg-slate-50/30">
      <p className="font-semibold text-gray-900 text-sm mb-3">
        Add a tip (optional)
      </p>

      <div className="grid grid-cols-4 gap-2">
        {[5, 10, 15].map((tip) => (
          <Button
            key={tip}
            buttonLabel={`$ ${tip}`}
            buttonFunction={() => handleTipSelect(tip)}
            buttonClasses={`
              w-full aspect-square rounded-xl border text-sm font-semibold
              flex items-center justify-center transition-all h-auto
              ${selectedTip === tip
                ? "bg-blue-100 border-blue-500 text-blue-500" 
                : "bg-white border-gray-300 text-gray-800 hover:bg-gray-50"}
            `}
          />
        ))}

        <Button
          buttonFunction={() => handleTipSelect(null)}
          buttonClasses={`
            w-full aspect-square rounded-xl border text-sm font-semibold
            flex flex-col items-center justify-center transition-all gap-1 h-auto
            ${selectedTip === null
              ? "bg-blue-100 border-blue-500 text-blue-500"
              : "bg-white border-gray-300 text-gray-800 hover:bg-gray-50"}
          `}
          buttonLabel={
            <>
              <i className="fa-regular fa-pen text-xs"></i>
              <span>custom</span>
            </>
          }
        />
      </div>

      {selectedTip === null && (
        <div className="mt-3">
          <Input
            inputPlaceholder="Enter custom tip amount (e.g., 12.50)"
            inputType="text" 
            inputMode="decimal"
            value={customTip}
            onChange={handleCustomTipChange}
            inputClasses="h-9 text-sm"
            autoComplete="off"
          />
        </div>
      )}
      
      {/* Show remaining amount if there are partial payments */}
      {hasPartialPayments && (
        <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-center justify-between text-sm mb-1">
            <span className="text-blue-800 font-medium">Remaining to Pay:</span>
            <span className="text-blue-900 font-bold">${remainingAmount.toFixed(2)}</span>
          </div>
          <p className="text-xs text-blue-700 mt-1">
            Paying the remaining amount will complete the bill
          </p>
        </div>
      )}

      {/* Payment Breakdown */}
      <div className="mt-3 p-3 bg-gray-50 border border-gray-200 rounded-lg">
        <div className="space-y-2">
          {/* Subtotal */}
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-700">Subtotal:</span>
            <span className="font-semibold text-gray-900">
              ${subtotalAmount.toFixed(2)}
            </span>
          </div>
          
          {/* Tip Amount - only show if tip is added */}
          {tipAmount > 0 && (
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-700">Tip:</span>
              <span className="font-semibold text-gray-900">
                ${tipAmount.toFixed(2)}
              </span>
            </div>
          )}
          
          {/* Divider */}
          <div className="border-t border-gray-300 my-2"></div>
          
          {/* Total */}
          <div className="flex items-center justify-between">
            <span className="text-gray-900 font-bold text-base">Total Amount:</span>
            <span className="font-bold text-gray-900 text-lg">
              ${payableAmount > 0 ? payableAmount.toFixed(2) : "0.00"}
            </span>
          </div>
        </div>
      </div>

      {/* Card Section */}
      <Elements stripe={stripePromise}>
        <CheckoutForm 
          paymentType={paymentType} 
          tableNumber={tableNumber}
          orderId={orderId} 
          tipAmount={tipAmount}   
          customPayAmount={remainingAmount}         
        />
      </Elements>

      <p className="text-[10px] text-gray-500 mt-3 text-center flex items-center justify-center gap-1">
        <i className="fa-solid fa-shield-check text-green-600"></i>
        100% Secure payments
      </p>
    </div>
  </div>
);

// Move SplitBillContent OUTSIDE of BillPay component
const SplitBillContent = ({
  selectedTip,
  handleTipSelect,
  customTip,
  setCustomTip,
  setOpenCustom,
  setOpenItems,
  setOpenEqual,
  setOpenEqualNew,
  orderDetails,
  hasSplitSelected
}) => (
  <div className="mt-0">
    <div className="rounded-xl border border-gray-200 px-3 shadow-sm pt-5 pb-3 bg-slate-50/30">
      <p className="font-semibold text-gray-900 text-sm mb-3">
        Add a tip (optional)
      </p>

      <div className="grid grid-cols-4 gap-2">
        {[5, 10, 15].map((tip) => (
          <Button
            key={tip}
            buttonLabel={`$ ${tip}`}
            buttonFunction={() => handleTipSelect(tip)}
            buttonClasses={`
              w-full aspect-square rounded-xl border text-sm font-semibold
              flex items-center justify-center transition-all h-auto
              ${selectedTip === tip
                ? "bg-blue-100 border-blue-300 text-blue-500"
                : "!bg-white border-gray-300 text-gray-800 hover:!bg-gray-50"}
            `}
          />
        ))}

        <Button
          buttonFunction={() => handleTipSelect(null)}
          buttonClasses={`
            w-full aspect-square rounded-xl border text-sm font-semibold
            flex flex-col items-center justify-center transition-all gap-1 h-auto
            ${selectedTip === null
              ? "bg-blue-100 border-blue-300 text-blue-500"
              : "bg-white border-gray-300 text-gray-800 hover:bg-gray-50"}
          `}
          buttonLabel={
            <>
              <i className="fa-regular fa-pen text-xs"></i>
              <span>custom</span>
            </>
          }
        />
      </div>

      {selectedTip === null && (
        <div className="mt-3">
          <Input
            inputPlaceholder="Enter custom tip amount"
            inputType="text"
            inputMode="decimal"
            value={customTip}
            onChange={(e) => setCustomTip(e.target.value)}
            inputClasses="h-9 text-sm"
            autoComplete="off"
          />
        </div>
      )}

      <div className="mt-4">
        <h5 className="text-lg text-[#232222] font-medium mb-1">Split payment</h5>
        <p className="text-base text-[#9FA1A3] mb-4 font-normal">
          You can pay for your share by choosing your preferred payment method below.
        </p>

        <div className="flex flex-col gap-2">
          <Button
            buttonLabel="Complete payment for your items"
            buttonClasses="w-full bg-[#F5F5F5] border border-[#D8D8D8] text-[14px] rounded-lg px-4 transition-all justify-start h-12 hover:bg-white"
            buttonLabelClasses="text-base font-semibold text-gray-900 text-left"
            buttonFunction={() => setOpenCustom(true)}
          />            
          {
            (() => {
              const count = orderDetails?.payment?.splits?.length > 0 && 
                orderDetails?.payment?.splits?.filter((item) => item.splitType !== 'split_item').length;
              return (
                <Button
                  buttonLabel="Pay for your items"
                  buttonClasses="w-full bg-[#F5F5F5] border border-[#D8D8D8] text-[14px] rounded-lg px-4 transition-all justify-start h-12 hover:bg-white disabled:opacity-50 disabled:cursor-not-allowed"
                  buttonLabelClasses="text-base font-semibold text-gray-900 text-left"
                  buttonFunction={() => setOpenItems(true)}
                  isDisable={count > 0}
                />
              );
            })()
          }    
          {/* <Button
            buttonLabel="Divide the bill equally"
            buttonClasses="w-full bg-[#F5F5F5] border border-[#D8D8D8] text-[14px] rounded-lg px-4 transition-all justify-start h-12 hover:bg-white"
            buttonLabelClasses="text-base font-semibold text-gray-900 text-left"
            buttonFunction={() => setOpenEqual(true)}
          /> */}
          <Button
            buttonLabel="Divide the bill equally "
            buttonClasses="w-full bg-[#F5F5F5] border border-[#D8D8D8] text-[14px] rounded-lg px-4 transition-all justify-start h-12 hover:bg-white"
            buttonLabelClasses="text-base font-semibold text-gray-900 text-left"
            buttonFunction={() => setOpenEqualNew(true)}
          />
          {/* <div className="relative">
            <Button
              buttonLabel="Divide the bill equally "
              buttonClasses="w-full bg-[#F5F5F5] border border-[#D8D8D8] text-[14px] rounded-lg px-4 transition-all justify-start h-12 hover:bg-white"
              buttonLabelClasses="text-base font-semibold text-gray-900 text-left"
              buttonFunction={() => setOpenEqualNew(true)}
            />           
            
            <div className="absolute top-0 right-0 mt-2 mr-2 flex items-center justify-center">
              <div className="bg-red-500 text-white text-xs px-2 py-1 rounded-full">
                New
              </div>
            </div>
          </div> */}
        </div>
      </div>
    </div>
  </div>
);

const BillPay = () => {
  const params = useParams();
  const tableNumber = params?.table;
  const orderId = params?.orderid;
  const inputRef = useRef(null); 
  const { getPath, config } = useBistroNavigation();
  const [selectedTip, setSelectedTip] = useState(0);
  const [customTip, setCustomTip] = useState("");
  const [activeTab, setActiveTab] = useState(0);
  const [openCustom, setOpenCustom] = useState(false);
  const [openItems, setOpenItems] = useState(false);
  const [openEqual, setOpenEqual] = useState(false);
  const [openEqualNew,setOpenEqualNew] = useState(false);

  const [paymentType, setPaymentType] = useState("full");
  const [orderDetails, setOrderDetails] = useState({});
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [splitProcess, setSplitProcess] = useState('');

  const tipAmount = customTip ? Number(customTip) : (selectedTip || 0);
  const hasPartialPayments = orderDetails?.payment?.splits?.length > 0;
  const totalPaidAmount = hasPartialPayments 
    ? orderDetails.payment.splits.reduce((sum, split) => {
        return sum + (split.amount || 0);
      }, 0)
    : 0;
  const remainingAmount = total - totalPaidAmount;  
  const subtotalAmount = hasPartialPayments ? remainingAmount : total; 
  const hasSplitSelected = splitProcess && ['custom_amount', 'split_item', 'split_amount'].includes(splitProcess);

  useEffect(() => {
    const fetchOrderDetails = async () => {
      setLoading(true);
      try {
        const result = await apiRequest("post", `order/order-detail`, {
          body: {
            tableNumber,
            orderId              
          },
        });
        if (result.status === 200) {
          const data = result?.data?.data || {};
          setOrderDetails(data);
          setTotal(data?.billAmount || 0);
          
          const splitType = data?.payment?.splitType;
          setSplitProcess(splitType || '');
          
          // Don't force tab selection - let user choose
          if (splitType && splitType !== 'full') {
            setActiveTab(1);
            setPaymentType('split');
          } else {
            setActiveTab(0);
            setPaymentType('full');
          }
        }
      } catch (error) {
        console.error("Error fetching order details:", error);
      } finally {
        setLoading(false);
      }
    };    
    fetchOrderDetails();
  }, [tableNumber, orderId]);  

  const handleTipSelect = useCallback((value) => {
    if (value === selectedTip && value !== null) {
      setSelectedTip(0);
      setCustomTip("");
      return;
    }
    
    setSelectedTip(value);
    
    if (value !== null) {
      setCustomTip("");
    }
  }, [selectedTip]);

  const handleCustomTipChange = useCallback((e) => {
    let value = e.target.value;
    value = value.replace(/[^\d.]/g, '');
    const parts = value.split('.');
    if (parts.length > 2) {
      value = parts[0] + '.' + parts.slice(1).join('');
    } 
    if (parts.length === 2 && parts[1].length > 2) {
      value = parts[0] + '.' + parts[1].substring(0, 2);
    }    
    setCustomTip(value);
    if (value && value !== '0' && value !== '0.') {
      setSelectedTip(null);
    }
  }, []);

  // Calculate payable amount - use remaining amount if there are partial payments
  const payableAmount = subtotalAmount + tipAmount;

  const handleTabChange = (tabIndex) => {
    // Don't prevent tab switching - always allow
    setActiveTab(tabIndex);
    setSelectedTip(0);
    setCustomTip("");
    
    if (tabIndex === 0) {
      setPaymentType('full');
    } else {
      setPaymentType('split');
    }
  };

  if (loading) {
    return <BillPaySkeleton />;
  }  

  return (
    <>    
      <div className="w-full max-w-sm mx-auto bg-white rounded-2xl shadow-sm overflow-hidden pb-6">
        {/* Header - Always visible */}
        <div className="border-b border-[#F4F4F4] flex-shrink-0">
          <div className="flex items-center justify-between gap-4 px-4 py-5">
            <Link 
              to={getPath((tableNumber && tableNumber !== undefined)?`/home/table/${tableNumber}`:`/home`)} 
              className="w-10 aspect-square overflow-hidden flex-shrink-0"
            >
              <Image src={config?.logo} classes={"object-cover"} />
            </Link>
            <div className="relative flex-1 min-w-0">
              <h3 className="font-semibold text-gray-900 text-base sm:text-lg">
                Table {(Object.keys(orderDetails).length > 0) ? orderDetails?.tableData?.tableNumber : ''}
              </h3>
            </div>
            <Button
              buttonLabel={"View Menu"}
              buttonLabelClasses={"font-medium text-sm text-black"}
              buttonClasses={"bg-transparent p-0 h-5 hover:bg-transparent flex-shrink-0"}
              buttonHasLink={true}
              buttonLink={getPath((tableNumber && tableNumber !== undefined)?`/menu/table/${tableNumber}`:`/menu`)}
            />
          </div>
        </div>

        {/* Empty State - Shows below header when no order */}
        {(!orderDetails || Object.keys(orderDetails).length === 0) ? (
          <div className="min-h-[60vh] flex items-center justify-center">
            <EmptyBillingState
              search={loading}
              tableNumber={tableNumber}
            />
          </div>
        ) : (
          <>
            {/* Order Items */}
            <div className="my-3 border-b border-gray-200 w-full">
              <div className="px-4 my-3">
                {orderDetails?.items?.length > 0 && orderDetails.items.map((item, index) => (
                  <div 
                    key={index} 
                    className="flex justify-between items-center py-2 text-sm sm:text-base"
                  >
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      <span className="bg-gray-100 text-gray-700 text-[10px] px-2 py-0.5 rounded-full font-medium flex-shrink-0">
                        {item.qty}x
                      </span>
                      <span className="text-sm font-medium text-black">
                        {item?.productName}
                        {item?.variantName ? ` - ${item?.variantName}` : ''}
                      </span>
                    </div>
                    <span className="text-sm font-medium text-black flex-shrink-0 ml-2">
                      ${item?.price?.toFixed(2)}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Payable Amount */}
            <div className="px-4 mb-4">
              <div className="flex justify-between items-center">
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-gray-900 text-sm">
                    {hasPartialPayments ? 'Total Bill Amount' : 'Payable amount'}
                  </p>
                  <p className="text-[10px] text-gray-500">Inclusive of all taxes and charges</p>
                </div>
                <p className="text-lg font-bold text-gray-900 flex-shrink-0 ml-4">
                  ${orderDetails?.billAmount?.toFixed(2)}
                </p>
              </div>
              
              {/* Show paid and remaining amounts if there are partial payments */}
              {hasPartialPayments && totalPaidAmount > 0 && (
                <div className="mt-3 pt-3 border-t border-gray-200">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-xs text-green-700 font-medium">Paid Amount</span>
                    <span className="text-sm font-semibold text-green-700">
                      ${totalPaidAmount.toFixed(2)}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-orange-700 font-medium">Remaining Amount</span>
                    <span className="text-sm font-bold text-orange-700">
                      ${remainingAmount.toFixed(2)}
                    </span>
                  </div>
                </div>
              )}
            </div>

            {/* Payment Tabs - Custom Implementation */}
            <div className="px-4">
              <div className="flex w-full justify-between gap-3 mb-4 flex-col">
                {/* Pay Fully Button - ALWAYS ENABLED and WORKING */}
                <button
                  type="button"
                  onClick={() => handleTabChange(0)}
                  className={`flex-1 py-3 rounded border text-lg font-semibold transition-all hover:bg-blue-100 hover:text-blue-600 ${
                    activeTab === 0
                      ? "bg-blue-500 text-white border-blue-500"
                    : "bg-white text-blue-500 border-gray-300 cursor-pointer hover:bg-blue-100 hover:text-blue-600"
                  }`}
                >
                  Pay in full
                </button>

                {/* Split Bill Button - ALWAYS ENABLED and WORKING */}
                <button
                  type="button"
                  onClick={() => handleTabChange(1)}
                    className={`flex-1 py-3 rounded border text-lg font-semibold transition-all hover:bg-blue-100 hover:text-blue-600 ${
                    activeTab === 1
                      ? "bg-blue-500 text-white border-blue-500"
                      : "bg-white text-blue-500 border-gray-300 cursor-pointer hover:bg-blue-100 hover:text-blue-600"
                  }`}
                >
                  Split payment
                </button>
              </div>

              {/* Tab Content */}
              <div>
                {activeTab === 0 ? (
                  <PaymentContent 
                    selectedTip={selectedTip}
                    handleTipSelect={handleTipSelect}
                    customTip={customTip}
                    handleCustomTipChange={handleCustomTipChange}
                    payableAmount={payableAmount}
                    paymentType={paymentType}
                    tableNumber={tableNumber}
                    orderId={orderId}
                    tipAmount={tipAmount}
                    remainingAmount={remainingAmount}
                    hasPartialPayments={hasPartialPayments}
                    subtotalAmount={subtotalAmount}
                  />
                ) : (
                  <SplitBillContent 
                    selectedTip={selectedTip}
                    handleTipSelect={handleTipSelect}
                    customTip={customTip}
                    setCustomTip={setCustomTip}
                    setOpenCustom={setOpenCustom}
                    setOpenItems={setOpenItems}
                    setOpenEqual={setOpenEqual}
                    setOpenEqualNew={setOpenEqualNew}
                    orderDetails={orderDetails}
                    hasSplitSelected={hasSplitSelected}
                  />
                )}
              </div>
            </div>
          </>
        )}
      </div>

      {/* Modals */}      
      <PayCustomAmount 
        isOpen={openCustom} 
        onClose={() => setOpenCustom(false)} 
        total={total}
        selectedTip={selectedTip} 
        stripePromise={stripePromise}
        paymentType={'custom_amount'} 
        tableNumber={tableNumber}
        orderId={orderId}
        customTip={customTip}
        previousTransactions={orderDetails?.miniStatement}
      />    
      <PayForYourItem 
        isOpen={openItems} 
        onClose={() => setOpenItems(false)} 
        total={total}
        selectedTip={selectedTip} 
        stripePromise={stripePromise}
        paymentType={'split_item'}
        tableNumber={tableNumber}
        orderId={orderId}
        customTip={customTip}
        orderItems={orderDetails?.items || []}
        previousTransactions={orderDetails?.miniStatement}
        paymentData={orderDetails?.payment}
      />    
      <DivideBillEqualy 
        isOpen={openEqual} 
        onClose={() => setOpenEqual(false)} 
        total={total}
        selectedTip={selectedTip} 
        stripePromise={stripePromise}
        paymentType={'split_amount'}
        tableNumber={tableNumber}
        orderId={orderId}
        customTip={customTip}
        previousTransactions={orderDetails?.miniStatement}
        numberOfPerson={orderDetails?.numberOfPerson}
        paymentData={orderDetails?.payment}
      /> 
      <DivideBillEqualyNew
        isOpen={openEqualNew} 
        onClose={() => setOpenEqualNew(false)} 
        total={total}
        selectedTip={selectedTip} 
        stripePromise={stripePromise}
        paymentType={'split_amount'}
        tableNumber={tableNumber}
        orderId={orderId}
        customTip={customTip}
        previousTransactions={orderDetails?.miniStatement}
        numberOfPerson={orderDetails?.numberOfPerson}
        paymentData={orderDetails?.payment}
      />           
    </>
  );
};

export default BillPay;